import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useEscapeLayer, useSaveShortcut } from './key-layers';
import { PersonalizationFrame } from './PersonalizationFrame';
import {
  FieldColumn,
  FieldError,
  FieldRow,
  FieldStatus,
  GhostButton,
  MONO_FONT,
  Row,
  RowHelp,
  RowLabel,
  RowText,
  SaveButton,
  TextInput,
  TYPE,
} from './primitives';
import { PERSONALIZATION_FILES } from './personalization';
import { usePersonalization } from '../../providers/PersonalizationProvider';

/**
 * Settings → Personalization: the display name (USER.md line 1) and a doorway
 * to the page where the files themselves are edited.
 */
export default function PersonalizationSection({
  onOpenFullView,
}: {
  /** Omitted ⇒ no doorway card. */
  readonly onOpenFullView?: () => void;
}) {
  return (
    <PersonalizationFrame>
      <DisplayNameRow />
      {onOpenFullView != null && (
        <EditorLinkCard type="button" onClick={onOpenFullView}>
          <StatusDot aria-hidden="true" />
          <LinkCardFiles>{PERSONALIZATION_FILES.join(' · ')}</LinkCardFiles>
          <LinkCardAction>Edit in Personalization →</LinkCardAction>
        </EditorLinkCard>
      )}
    </PersonalizationFrame>
  );
}

const MAX_DISPLAY_NAME = 64;

type RowStatus =
  | { kind: 'idle' | 'saving' | 'saved' }
  | { kind: 'error'; message: string };

function DisplayNameRow() {
  const api = usePersonalization();
  // The page editor may hold a USER.md draft (dirty or not) that its next Save
  // writes; two writers to one file would revert each other, so stand down.
  const blocked: string | null =
    api.drafts['USER.md'] !== undefined
      ? 'USER.md has unsaved changes in Personalization — save or discard those first.'
      : api.snapshot?.unreadable.includes('USER.md')
        ? "USER.md couldn't be read on the agent, so the name can't be changed from here."
        : null;
  const ready = api.status === 'ready' && blocked === null;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<RowStatus>({ kind: 'idle' });
  const inputRef = useRef<HTMLInputElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);

  const trimmed = draft.trim();
  const tooLong = trimmed.length > MAX_DISPLAY_NAME;
  const valid = trimmed.length > 0 && !tooLong;
  const dirty = trimmed !== api.displayName;

  const startEdit = (): void => {
    setDraft(api.displayName);
    setStatus({ kind: 'idle' });
    setEditing(true);
  };
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const canSave = useCallback(
    (): boolean => editing && valid && ready && status.kind !== 'saving',
    [editing, valid, ready, status.kind],
  );

  const save = useCallback(async (): Promise<void> => {
    if (!canSave()) return;
    if (!dirty) {
      setEditing(false);
      setStatus({ kind: 'idle' });
      return;
    }
    setStatus({ kind: 'saving' });
    try {
      await api.saveDisplayName(trimmed);
      setEditing(false);
      setStatus({ kind: 'saved' });
    } catch (err) {
      setStatus({
        kind: 'error',
        message:
          err instanceof Error && err.message.length > 0
            ? err.message
            : 'Could not save your name. Try again.',
      });
    }
  }, [api, canSave, dirty, trimmed]);

  const discard = useCallback((): void => {
    setEditing(false);
    setStatus({ kind: 'idle' });
  }, []);

  useSaveShortcut(
    editing,
    () => {
      if (!canSave()) return false;
      void save();
      return true;
    },
    () => columnRef.current,
  );
  useEscapeLayer(editing, discard, () => columnRef.current);

  let message: React.ReactNode = null;
  if (blocked !== null) message = blocked;
  else if (status.kind === 'error') {
    message = <FieldError>{status.message}</FieldError>;
  } else if (editing && trimmed.length === 0) message = 'Name cannot be empty.';
  else if (editing && tooLong) {
    message = `Use ${MAX_DISPLAY_NAME} characters or fewer.`;
  } else if (status.kind === 'saved' && !editing && !dirty) {
    message = 'Saved to USER.md.';
  }

  return (
    <Row>
      <RowText>
        <RowLabel as="label" htmlFor="settings-display-name">
          Display name
        </RowLabel>
        <RowHelp>
          How you appear across the terminal. Written to USER.md.
        </RowHelp>
      </RowText>
      <FieldColumn ref={columnRef}>
        <FieldRow
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <TextInput
            id="settings-display-name"
            ref={inputRef}
            value={editing ? draft : api.displayName}
            readOnly={!editing}
            disabled={!ready}
            maxLength={MAX_DISPLAY_NAME * 2}
            aria-invalid={editing && !valid}
            onChange={(e) => {
              setDraft(e.target.value);
              if (status.kind !== 'idle' && status.kind !== 'saving') {
                setStatus({ kind: 'idle' });
              }
            }}
          />
          {editing ? (
            <SaveButton
              type="submit"
              disabled={!valid || !ready || status.kind === 'saving'}
            >
              {status.kind === 'saving' ? 'Saving…' : 'Save'}
            </SaveButton>
          ) : (
            <GhostButton type="button" onClick={startEdit} disabled={!ready}>
              Edit
            </GhostButton>
          )}
        </FieldRow>
        {/* One message at a time: a live region reads its whole content. */}
        <FieldStatus role="status" aria-live="polite">
          {message}
        </FieldStatus>
      </FieldColumn>
    </Row>
  );
}

const EditorLinkCard = styled.button`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  width: 100%;
  margin: 4px 0 16px;
  padding: 12px 14px;
  border: 1px solid rgba(var(--accent-color-rgb), 0.35);
  border-radius: 8px;
  background: rgba(var(--accent-color-rgb), 0.06);
  color: var(--text-primary);
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: rgba(var(--accent-color-rgb), 0.6);
    background: rgba(var(--accent-color-rgb), 0.1);
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 2px;
  }
`;

const LinkCardFiles = styled.span`
  flex: 1 1 auto;
  min-width: 300px;
  font-family: ${MONO_FONT};
  font-size: 11px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LinkCardAction = styled.span`
  margin-left: auto;
  padding-left: 12px;
  flex-shrink: 0;
  ${TYPE.body}
  font-weight: 500;
  color: var(--accent-color);
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--accent-color);
  box-shadow: 0 0 6px rgba(var(--accent-color-rgb), 0.8);
`;
